import { requestBuilder } from '../../http/RequestBuilder';

export type InterestRateTierName = 'STANDARD' | 'PREMIUM';

export interface InterestRateScheduleWindow {
    validFrom: string;
    validTo?: string;
    rateBps: number;
    apyDisplay: string;
}

export interface PendingFutureRateWindow {
    tier: InterestRateTierName;
    validFrom: string;
    validTo?: string;
    rateBps: number;
    apyDisplay: string;
    scheduleKey: string;
}

export interface InterestRateScheduleResponse {
    cutoverDate: string;
    currentBankPostedDate: string;
    tiers: Record<InterestRateTierName, InterestRateScheduleWindow[]>;
    pendingFutureWindows: PendingFutureRateWindow[];
}

export interface InterestRateAccountsByTier {
    tier: string;
    accountCount: number;
    pendingFutureChangeCount: number;
    missingInterestProductCount: number;
    expectedRateBps: number | null;
    expectedApyDisplay: string | null;
}

export interface InterestRateReconMismatch {
    bankAccountId: string;
    syncteraAccountId: string;
    membershipId: string;
    expectedBps: number;
    actualBps: number | null;
    tier: string;
    futureValidFrom?: string | null;
    futureExpectedBps?: number | null;
    futureActualBps?: number | null;
}

export interface InterestRateReconSnapshot {
    id: string;
    startedAt: string;
    finishedAt: string;
    checkedCount: number;
    outOfSyncCount: number;
    missingInterestProductCount: number;
    unresolvedFailedLogCount: number;
    mismatches: InterestRateReconMismatch[];
}

export interface InterestRateAuditResponse {
    currentBankPostedDate: string;
    activeDepositoryAccountCount: number;
    accountsWithMembershipCount: number;
    accountsMissingProductPeriodCount: number;
    accountsByTier: InterestRateAccountsByTier[];
    unresolvedFailedLogCount: number;
    lastRecon: InterestRateReconSnapshot | null;
}

export interface InterestRateChangeLogListItem {
    id: string;
    bankAccountId: string;
    membershipId: string;
    syncteraInterestProductId: string | null;
    tier: string;
    rateBps: number;
    apyDisplay: string;
    validFrom: string;
    reason: string;
    triggerKey: string;
    productLogId: string | null;
    tierScheduleKey: string | null;
    stripeEventId: string | null;
    workflowId: string | null;
    status: string;
    error: string | null;
    syncteraRequest: unknown;
    syncteraResponse: unknown;
    createdAt: string;
    updatedAt: string | null;
}

export interface InterestRateChangeLogListResponse {
    total: number;
    limit: number;
    offset: number;
    items: InterestRateChangeLogListItem[];
}

export interface InterestRateChangeLogsQuery {
    status?: string;
    reason?: string;
    tier?: string;
    membershipId?: string;
    bankAccountId?: string;
    limit?: number;
    offset?: number;
}

export interface MassTierChangeRequest {
    tier: InterestRateTierName;
    effectivePostedDate: string;
}

export interface RetryMembershipInterestRateSyncRequest {
    changeLogId: string;
}

export interface RetryMembershipInterestRateSyncResponse {
    workflowID: string;
    membershipId: string;
    validFrom: string;
    originalValidFrom: string;
    productLogId?: string;
    clamped: boolean;
}

export type ProductLogHistoryMismatchReason =
    | 'NO_PRODUCT_LOG'
    | 'NO_CHANGE_LOG'
    | 'NO_RATES_IN_CHANGE_LOG'
    | 'ARRAY_MISMATCH';

export interface ProductLogHistoryPeriodSummary {
    productId: string;
    tier: string | null;
    postedDateFrom: string;
    postedDateTo: string | null;
}

export interface ProductLogHistoryMismatch {
    bankAccountId: string;
    syncteraAccountId: string;
    membershipId: string;
    reason: ProductLogHistoryMismatchReason;
    changeLogId: string | null;
    changeLogCreatedAt: string | null;
    firstDiffValidFrom: string | null;
    expectedBpsAtDiff: number | null;
    actualBpsAtDiff: number | null;
    expectedRates: unknown[] | null;
    actualRates: unknown[] | null;
    productLogPeriods: ProductLogHistoryPeriodSummary[];
}

export interface ProductLogHistoryReconResult {
    checkedCount: number;
    outOfSyncCount: number;
    missingProductLogCount: number;
    missingChangeLogCount: number;
    missingRatesInChangeLogCount: number;
    mismatches: ProductLogHistoryMismatch[];
}

export interface WorkflowIdResponse {
    workflowID: string;
}

const buildQueryString = (params: InterestRateChangeLogsQuery): string => {
    const search = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
        if (value != null && value !== '' && typeof value !== 'object') {
            search.set(key, String(value));
        }
    }
    const qs = search.toString();
    return qs.length > 0 ? `?${qs}` : '';
};

export const useGetInterestRateSchedule = requestBuilder.query<
    InterestRateScheduleResponse,
    Record<string, never>
>({
    endpoint: '/api/admin/bank-ops/interest-rate-schedule',
    queryKey: '/api/admin/bank-ops/interest-rate-schedule',
});

export const useGetInterestRateAudit = requestBuilder.query<
    InterestRateAuditResponse,
    Record<string, never>
>({
    endpoint: '/api/admin/bank-ops/interest-rate-audit',
    queryKey: '/api/admin/bank-ops/interest-rate-audit',
});

export const useGetInterestRateChangeLogs = requestBuilder.query<
    InterestRateChangeLogListResponse,
    InterestRateChangeLogsQuery
>({
    endpoint: (params) =>
        `/api/admin/bank-ops/interest-rate-change-logs${buildQueryString(params)}`,
    queryKey: (params) =>
        `/api/admin/bank-ops/interest-rate-change-logs${buildQueryString(params)}`,
});

export const useGetInterestRateRecon = requestBuilder.query<
    InterestRateReconSnapshot,
    Record<string, never>
>({
    endpoint: '/api/admin/bank-ops/interest-rate-recon',
    queryKey: '/api/admin/bank-ops/interest-rate-recon',
});

export const useGetInterestRateProductLogHistoryRecon = requestBuilder.query<
    ProductLogHistoryReconResult,
    Record<string, never>
>({
    endpoint: '/api/admin/bank-ops/interest-rate-product-log-history-recon',
    queryKey: '/api/admin/bank-ops/interest-rate-product-log-history-recon',
});

export const useTriggerMassTierChange = requestBuilder.mutation<
    WorkflowIdResponse,
    MassTierChangeRequest
>({
    endpoint: '/api/admin/bank-ops/interest-rate-sync/mass-tier-change',
    method: 'POST',
});

export const useRetryMembershipInterestRateSync = requestBuilder.mutation<
    RetryMembershipInterestRateSyncResponse,
    RetryMembershipInterestRateSyncRequest
>({
    endpoint: '/api/admin/bank-ops/interest-rate-sync/retry-membership',
    method: 'POST',
});
